import { Box, Link, Typography } from "@mui/material";
import { ReactFlowProvider } from "@xyflow/react";
import { isAxiosError } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  useValueChain,
  useValueChains,
} from "../../hooks/queries/useValueChain";
import LogoLoader from "../common/LogoLoader";
import { monospaceStyle } from "../../theme";
import { ChainCard, ChainSide, ValueChain } from "../../types/valueChain";
import {
  PageChain,
  PageChainContext,
  chainDetailOf,
  toChainDetail,
} from "../KindDetail/kindChain";
import {
  entityPath,
  readDetailRelationship,
  withoutDetailRelationship,
} from "../../utils/entityAddress";
import EdgeDetailOverlay from "../Layout/EdgeDetailOverlay";
import EntityDetailOverlay from "../Layout/EntityDetailOverlay";
import ChainCanvas, { ExportPngButton, FitButton } from "./ChainCanvas";
import ChainLegend from "./ChainLegend";
import { ChainActions, ChainActionsContext } from "./chainActions";
import {
  ChainLensContext,
  ChainLensView,
  chainCountries,
  countryName,
  coverageFlags,
  coverageRows,
} from "./chainLens";
import CoverageReport from "./CoverageReport";
import JurisdictionSettings from "./JurisdictionSettings";
import {
  cardName,
  drawMergedChain,
  highlightMakers,
  highlightOf,
  visibleChain,
} from "./chainModel";
import { Arrangement } from "./chainLayout";
import { areaGrouping, leftOutNote, leftOutOf, tierRead } from "./tierModel";
import { TreeActions, TreeActionsContext } from "./treeActions";
import TreeCanvas from "./TreeCanvas";
import {
  TREE_LEVELS,
  VisibleTree,
  buildTree,
  trailTo,
  treeRowId,
  visibleTree,
} from "./treeModel";
import ValueChainControls, {
  MakerHighlight,
  ViewSwitch,
} from "./ValueChainControls";
import ValueChainRootField from "./ValueChainRootField";
import { useValueChainState } from "./useValueChainState";
import {
  ACTIVE_DIRECTIONS,
  ChainCardRef,
  expansionParams,
  isOpened,
  readParams,
  toValueChainParams,
  toggleOpened,
} from "./valueChainState";

// What the evidence caption says of every count on the page
export const ON_RECORD_REMINDER =
  "counts are what the sources state, never totals";

// What the page says when a read fails: the backend's reasons for refused
// parameters (a direction not served yet), its error, or a plain line
export const chainErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const body: unknown = error.response?.data;
    if (body && typeof body === "object") {
      const { details, error: reason } = body as {
        details?: { message?: unknown }[];
        error?: unknown;
      };
      const messages = (Array.isArray(details) ? details : [])
        .map((detail) => detail?.message)
        .filter((message): message is string => typeof message === "string");
      if (messages.length > 0) {
        return messages.join("; ");
      }
      if (typeof reason === "string") {
        return reason;
      }
    }
  }
  return "The value chain could not be loaded.";
};

// A number, in the monospace face
const Num: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

// The evidence caption of the chain: the articles its counts come from, the
// graph's date (its version on hover), and that no count is a total
const ChainEvidence: React.FC<{ chain: ValueChain }> = ({ chain }) => {
  const { articles, graph_date: date, graph_version: version } =
    chain.evidence;
  return (
    <>
      On record in <Num>{articles}</Num>{" "}
      {articles === 1 ? "article" : "articles"} · graph{" "}
      <Box
        component="span"
        title={`Backbone ${version ?? "unversioned"}`}
        sx={monospaceStyle}
      >
        {date ?? version ?? "unversioned"}
      </Box>{" "}
      · <span>{ON_RECORD_REMINDER}</span>
    </>
  );
};

// What the evidence line adds for a thin side of a chain: none or one kind
// of part stated inside the root, none or one kind it is built into
const thinSide = (chain: ValueChain, side: ChainSide): string | null => {
  const name = cardName(chain.root);
  const count = chain.cards.filter((card) => card.side === side).length;
  if (side === "parts") {
    if (count === 0) {
      return `Nothing is stated inside ${name} on record.`;
    }
    return count === 1
      ? `One kind of part is stated inside ${name} on record.`
      : null;
  }
  if (count === 0) {
    return `${name} is built into nothing on record.`;
  }
  return count === 1 ? `${name} is built into one kind on record.` : null;
};

// What the evidence line adds for a thin chain, for each side the read
// describes
export const thinNote = (chain: ValueChain): string | null => {
  const sides: ChainSide[] =
    chain.direction === "both"
      ? ["hosts", "parts"]
      : [chain.direction === "hosts" ? "hosts" : "parts"];
  const notes = sides
    .map((side) => thinSide(chain, side))
    .filter((note): note is string => note !== null);
  return notes.length > 0 ? notes.join(" ") : null;
};

// The way down the tree view, above its canvas: the root, which closes the
// open subsystem, the subsystem, and each open kind, which moves the window
// back to it
const TreeTrail: React.FC<{
  tree: VisibleTree;
  actions: TreeActions;
}> = ({ tree, actions }) => {
  const steps: { label: string; onClick: () => void }[] = [
    { label: cardName(tree.tree.root), onClick: () => actions.onBranch(null) },
  ];
  if (tree.branch) {
    const name = tree.branch.name;
    steps.push({ label: name, onClick: () => actions.onTrail([]) });
    tree.trail.forEach((id, index) => {
      const card = tree.tree.homes.get(id)?.card;
      steps.push({
        label: card ? cardName(card) : id,
        onClick: () => actions.onTrail(tree.trail.slice(0, index + 1)),
      });
    });
  }
  return (
    <Box
      component="nav"
      aria-label="Open branch"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        columnGap: 0.75,
        fontSize: "12.5px",
        color: "text.disabled",
      }}
    >
      {steps.map((step, index) => (
        <React.Fragment key={`${index}:${step.label}`}>
          {index > 0 && <span aria-hidden>›</span>}
          {index === steps.length - 1 ? (
            <Box component="span" aria-current="location" sx={{ color: "text.primary" }}>
              {step.label}
            </Box>
          ) : (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={step.onClick}
              sx={{ font: "inherit" }}
            >
              {step.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

// The value chain: a kind or a class read through the composition its
// sources state, drawn as cards in columns on a pan-and-zoom canvas. What it
// shows is held in the address (see valueChainState).
const ValueChainView: React.FC = () => {
  const [state, change] = useValueChainState();

  // A relationship opened from the relation table of the open detail lies
  // over that detail; closing it shows the detail again
  const [searchParams, setSearchParams] = useSearchParams();
  const relationshipId = readDetailRelationship(searchParams);

  const request = useMemo(() => toValueChainParams(state), [state]);
  const { data, error, isPending, isFetching } = useValueChain(request);

  // The opened cards are read under the same jurisdiction settings and
  // switches
  const shared = useMemo(
    () =>
      readParams({
        lens: state.lens,
        home: state.home,
        trusted: state.trusted,
        rule: state.rule,
        unnamed: state.unnamed,
        classes: state.classes,
        typical: state.typical,
      }),
    [
      state.lens,
      state.home,
      state.trusted,
      state.rule,
      state.unnamed,
      state.classes,
      state.typical,
    ],
  );
  // The side the tree is grown from: what the root is built into when that
  // is read, else its parts; null outside the tree
  const treeSide: ChainSide | null =
    state.view === "tree" ? (state.direction === "hosts" ? "hosts" : "parts") : null;
  // A card opened in the tree of hosts is read for its own hosts
  const expansions = useMemo(
    () =>
      state.opened.map((card) =>
        expansionParams(card, shared, treeSide === "hosts" ? "hosts" : "parts"),
      ),
    [state.opened, shared, treeSide],
  );
  const reads = useValueChains(expansions);

  // The collapsed rows shown whole, by their ids; forgotten when the root
  // changes
  const [shownWhole, setShownWhole] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const rootKey = state.root ? `${state.root.type}:${state.root.id}` : "";
  useEffect(() => {
    setShownWhole(new Set());
  }, [rootKey]);

  // The grouped and the matrix arrangements lay the reads out by tier and
  // group each column by area; the graph keeps the read's columns, and the
  // tree draws its own
  const tree = treeSide !== null;
  const arrangement: Arrangement | null =
    state.view === "graph" || state.view === "tree" ? null : state.view;
  const visible = useMemo(() => {
    if (!data) {
      return null;
    }
    const byTier = (read: ValueChain) => (arrangement ? tierRead(read) : read);
    const drawn = drawMergedChain(
      byTier(data),
      state.opened,
      reads.map((read) => read && byTier(read)),
    );
    return visibleChain(
      drawn,
      shownWhole,
      new Set(state.opened.map((card) => card.id)),
      undefined,
      data.direction === "both" && state.partsStub === "collapsed",
      arrangement
        ? areaGrouping([drawn.root, ...Array.from(drawn.cards.values())])
        : null,
    );
  }, [data, state.opened, state.partsStub, reads, shownWhole, arrangement]);

  // The tree of subsystems of the reads, and what of it is shown: the open
  // subsystem down its trail
  const chainTree = useMemo(
    () =>
      data && treeSide
        ? buildTree(drawMergedChain(data, state.opened, reads), treeSide)
        : null,
    [data, treeSide, state.opened, reads],
  );
  const shownTree = useMemo(
    () =>
      chainTree
        ? visibleTree(
            chainTree,
            { branch: state.branch, trail: state.trail },
            shownWhole,
          )
        : null,
    [chainTree, state.branch, state.trail, shownWhole],
  );
  // A kind shown on request, which the tree's canvas brings into view
  const [treeFocus, setTreeFocus] = useState<string | null>(null);

  const treeActions: TreeActions = useMemo(
    () => ({
      onBranch: (branch) => {
        setTreeFocus(null);
        change({ branch, trail: [] });
      },
      onTrail: (trail, read) => {
        setTreeFocus(null);
        change(
          read && !isOpened(state.opened, read)
            ? { trail, opened: [...state.opened, read] }
            : { trail },
        );
      },
      onShow: (card, subsystem) => {
        if (!chainTree) {
          return;
        }
        const trail = trailTo(chainTree, card, subsystem);
        // The level it is in shows every kind, so that it is among them
        const row = treeRowId(subsystem, trail[trail.length - 1] ?? null);
        setShownWhole((current) => new Set(Array.from(current).concat(row)));
        setTreeFocus(card);
        change({ branch: subsystem, trail });
      },
    }),
    [change, state.opened, chainTree],
  );

  // The cards of rank l1 the tier arrangements and the tree leave out, and
  // what they rested on
  const leftOut = useMemo(
    () => (data && (arrangement || tree) ? leftOutNote(leftOutOf(data)) : null),
    [data, arrangement, tree],
  );

  // The focus view's maker highlight: the makers it may be set to, and what
  // it keeps lit
  const makers = useMemo(() => (data ? highlightMakers(data) : []), [data]);
  const highlight = useMemo(
    () => (visible ? highlightOf(visible, state.maker) : null),
    [visible, state.maker],
  );

  const actions: ChainActions = useMemo(
    () => ({
      opened: state.opened,
      loading: state.opened.filter((_card, index) => !reads[index]),
      detail: state.detail,
      direction: state.direction,
      partsDepth: state.partsDepth,
      hostsDepth: state.hostsDepth,
      onToggle: (card: ChainCardRef) =>
        change({ opened: toggleOpened(state.opened, card) }),
      onOpenDetail: (detail) =>
        change({ detail: chainDetailOf(detail, [data, ...reads]) }),
      onShowAll: (row) =>
        setShownWhole((current) => new Set(Array.from(current).concat(row))),
      onOpenParts: () => change({ partsStub: "open" }),
      onDirectionChange: (direction) => change({ direction }),
      // As the root field does: the new root in its own default direction
      onCentre: (card: ChainCardRef) =>
        change({ root: { type: card.type, id: card.id } }),
    }),
    [
      state.opened,
      state.detail,
      state.direction,
      state.partsDepth,
      state.hostsDepth,
      reads,
      change,
      data,
    ],
  );

  // What the overlay's kind and class views know of the page's chain; make
  // root may name the direction to read the new root in
  const pageChain: PageChain = useMemo(
    () => ({
      chain: data ?? null,
      onMakeRoot: (root, direction) =>
        change(direction ? { root, direction } : { root }),
    }),
    [data, change],
  );

  // What the lens adds to the cards: the same cards, restyled
  const lensView: ChainLensView = useMemo(
    () => ({
      lens: state.lens,
      home:
        state.lens === "jurisdiction" ? (data?.home_jurisdiction ?? null) : null,
      trusted:
        state.lens === "jurisdiction" ? (data?.trusted_jurisdictions ?? []) : [],
      flags:
        state.lens === "jurisdiction" ? coverageFlags([data, ...reads]) : new Map(),
    }),
    [state.lens, data, reads],
  );

  // The country codes the makers of the chain are at home in
  const countries = useMemo(
    () => chainCountries([data, ...reads]),
    [data, reads],
  );

  const jurisdictionLens = state.lens === "jurisdiction";
  // The report holds the rows of every read the page holds, the opened
  // cards' included
  const coverage = useMemo(
    () =>
      jurisdictionLens && data?.home_jurisdiction
        ? coverageRows([data, ...reads])
        : null,
    [jurisdictionLens, data, reads],
  );
  // Every card the page holds, by id: the report's levels and links
  const cards = useMemo(() => {
    const byId = new Map<string, ChainCard>();
    for (const read of [data, ...reads]) {
      for (const card of read ? [read.root, ...read.cards] : []) {
        if (!byId.has(card.id)) {
          byId.set(card.id, card);
        }
      }
    }
    return byId;
  }, [data, reads]);

  const thin = data ? thinNote(data) : null;
  const focus = data?.direction === "both";
  const home = jurisdictionLens ? (data?.home_jurisdiction ?? null) : null;
  const pathColours = useMemo(
    () => (visible ? Array.from(new Set(visible.pathColours.values())) : []),
    [visible],
  );

  return (
    <ReactFlowProvider>
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          boxSizing: "border-box",
          px: 3,
          pt: "22px",
          pb: 5,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          backgroundColor: "background.default",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h5" component="h1" sx={{ m: 0 }}>
              Value chain
            </Typography>
            {data && (
              <Typography variant="caption" color="text.secondary">
                <Box component="span" sx={{ color: "text.primary" }}>
                  {cardName(data.root)}
                </Box>
                {home && (
                  <>
                    {" "}
                    · home jurisdiction{" "}
                    <Box
                      component="span"
                      title={home}
                      sx={{ color: "text.primary" }}
                    >
                      {countryName(home)}
                    </Box>
                  </>
                )}
              </Typography>
            )}
          </Box>
          {state.root && (
            <Box component="section" aria-label="Evidence">
              <Typography
                role="status"
                variant="caption"
                component="p"
                sx={{ m: 0, color: error ? "error.main" : "text.disabled" }}
              >
                {error ? (
                  chainErrorMessage(error)
                ) : data ? (
                  <>
                    <ChainEvidence chain={data} />
                    {thin && <> · {thin}</>}
                  </>
                ) : (
                  "Reading the chain…"
                )}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          role="toolbar"
          aria-label="Chain controls"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <ValueChainRootField
            root={state.root}
            rootLabel={data?.root.label ?? null}
            rootId={data?.root.id ?? state.root?.id ?? null}
            onSubmit={(root) => change({ root })}
          />
          <ValueChainControls
            direction={state.direction}
            onDirectionChange={(direction) => change({ direction })}
            directions={tree ? ACTIVE_DIRECTIONS : undefined}
            partsDepth={state.partsDepth}
            onPartsDepthChange={(partsDepth) => change({ partsDepth })}
            hostsDepth={state.hostsDepth}
            onHostsDepthChange={(hostsDepth) => change({ hostsDepth })}
            lens={state.lens}
            onLensChange={(lens) => change({ lens })}
            unnamed={state.unnamed}
            onUnnamedChange={(unnamed) => change({ unnamed })}
            classes={state.classes}
            onClassesChange={(classes) => change({ classes })}
            typical={state.typical}
            onTypicalChange={(typical) => change({ typical })}
          />
          <Box sx={{ ml: "auto", display: "flex", gap: 0.5, alignItems: "center" }}>
            <ViewSwitch
              view={state.view}
              onViewChange={(view) => change({ view })}
            />
            {visible && <FitButton />}
            {focus && !tree
              ? makers.length > 0 && (
                  <MakerHighlight
                    makers={makers}
                    maker={state.maker}
                    onMakerChange={(maker) => change({ maker })}
                  />
                )
              : visible &&
                data && <ExportPngButton rootId={data.root.id} />}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 480,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "flex-start" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            {tree && shownTree && (
              <TreeTrail tree={shownTree} actions={treeActions} />
            )}
            <Box
              component="section"
              aria-label="Chain"
              sx={{
                flex: 1,
                minHeight: 480,
                position: "relative",
                border: 1,
                borderColor: "divider",
                borderRadius: (theme) => `${theme.radius.surface}px`,
                overflow: "hidden",
                backgroundColor: "background.paper",
                opacity: isFetching && data ? 0.6 : 1,
                transition: "opacity 120ms",
              }}
            >
              {!state.root ? (
                <Typography
                  color="text.secondary"
                  sx={{ p: 4, textAlign: "center" }}
                >
                  Name a kind or a class to draw the chain of its parts.
                </Typography>
              ) : error ? null : isPending || !visible ? (
                <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", p: 3 }}>
                  <LogoLoader size="view" surface="paper" label="Loading the chain" />
                </Box>
              ) : tree && shownTree ? (
                <ChainActionsContext.Provider value={actions}>
                  <ChainLensContext.Provider value={lensView}>
                    <TreeActionsContext.Provider value={treeActions}>
                      <TreeCanvas
                        key={`${state.lens}:tree`}
                        tree={shownTree}
                        focus={treeFocus}
                      />
                    </TreeActionsContext.Provider>
                  </ChainLensContext.Provider>
                </ChainActionsContext.Provider>
              ) : (
                <ChainActionsContext.Provider value={actions}>
                  <ChainLensContext.Provider value={lensView}>
                    {/* A lens changes the cards' sizes, and a layout their
                        places: laid out afresh */}
                    <ChainCanvas
                      key={`${state.lens}:${state.view}`}
                      chain={visible}
                      highlight={highlight}
                      arrangement={arrangement}
                    />
                  </ChainLensContext.Provider>
                </ChainActionsContext.Provider>
              )}
            </Box>

            {visible && (
              <ChainLegend
                lens={state.lens}
                focus={focus && !tree}
                pathColours={pathColours}
              />
            )}
            {tree && shownTree && data && (
              <Typography
                variant="caption"
                component="p"
                aria-label="Tree"
                sx={{ m: 0, color: "text.disabled" }}
              >
                {treeSide === "hosts" ? (
                  <>
                    Each kind {cardName(data.root)} is stated to be built into
                    starts a branch, and the branches are grouped by their
                    curated area: the domain of a platform, the area of
                    equipment or a module. A kind hangs under the part it is
                    most often stated to hold; a dashed line marks another
                    part on the canvas it holds too. A kind in several groups
                    appears in each, its hosts listed at home. The most stated
                    come first. One group is open at a time, and at most{" "}
                    {TREE_LEVELS} of its levels.
                  </>
                ) : (
                  <>
                    Each kind stated directly in {cardName(data.root)} starts a
                    branch, and the branches are grouped into subsystems by
                    their curated area. A kind hangs under the host that states
                    it most often; a dashed line marks another host on the
                    canvas that states it too. A kind used in several
                    subsystems appears in each, its parts listed at home. One
                    subsystem is open at a time, and at most {TREE_LEVELS} of
                    its levels.
                  </>
                )}
                {treeSide === "parts" && shownTree.tree.insideCarried > 0 && (
                  <>
                    {" "}
                    Not shown: {shownTree.tree.insideCarried}{" "}
                    {shownTree.tree.insideCarried === 1 ? "kind" : "kinds"}{" "}
                    found only inside the platforms it carries, each a chain
                    of its own.
                  </>
                )}
                {data.direction === "both" && (
                  <>
                    {" "}
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => change({ direction: "hosts" })}
                      sx={{ font: "inherit", verticalAlign: "baseline" }}
                    >
                      Show what it is built into
                    </Link>
                  </>
                )}
                {leftOut && <> {leftOut}</>}
              </Typography>
            )}
            {arrangement && data && (
              <Typography
                variant="caption"
                component="p"
                aria-label="Tiers"
                sx={{ m: 0, color: "text.disabled" }}
              >
                Columns are tiers: the fewest steps from {cardName(data.root)},
                so tier 1 is everything stated directly in it, whatever else
                it is stated inside. Only the edges from one tier to the next
                are drawn. Areas are the backbone's second level, the most
                stated first within each level; a card that is a group or an
                area itself stands for products filed there alone.
                {leftOut && <> {leftOut}</>}
              </Typography>
            )}
          </Box>

          {/* The jurisdiction lens's settings, beside the chain */}
          {jurisdictionLens && (
            <JurisdictionSettings
              home={state.home}
              onHomeChange={(next) => change({ home: next })}
              trusted={state.trusted}
              onTrustedChange={(trusted) => change({ trusted })}
              rule={state.rule}
              onRuleChange={(rule) => change({ rule })}
              countries={countries}
            />
          )}
        </Box>

        {coverage && state.root && (
          <CoverageReport
            rows={coverage}
            rootId={state.root.id}
            rootName={data ? cardName(data.root) : null}
            rootCard={data?.root.id ?? null}
            home={data?.home_jurisdiction ?? null}
            trusted={(data?.trusted_jurisdictions.length ?? 0) > 0}
            cards={cards}
            onOpenDetail={actions.onOpenDetail}
          />
        )}

        {/* The relationship the address names, over the detail */}
        {relationshipId && (
          <EdgeDetailOverlay
            relationshipId={relationshipId}
            onClose={() =>
              setSearchParams((current) => withoutDetailRelationship(current))
            }
            // The chain holds a detail only with its type, which the ends
            // of a relationship do not carry: they open on their own page
            onEntityClick={(id) => window.open(entityPath(id), "_blank")}
          />
        )}

        {/* The detail the address names, beside the chain, which stays */}
        {state.detail && !relationshipId && (
          <PageChainContext.Provider value={pageChain}>
            <EntityDetailOverlay
              entityId={state.detail.id}
              entityType={state.detail.type}
              onClose={() => change({ detail: null })}
              onEntityClick={(id, type) => {
                const detail = toChainDetail(id, type);
                if (detail) {
                  change({ detail });
                }
              }}
            />
          </PageChainContext.Provider>
        )}
      </Box>
    </ReactFlowProvider>
  );
};

export default ValueChainView;
